<template>
	<div class="formgroup-container">
		<div class="formgroup-heading">
			<strong>{{ $t(`form${capitalize(field.name)}`) }}<span v-if="field.required"> *</span></strong>
		</div>
		<div class="checkboxes">
			<label v-for="value in field.options" :key="value" :for="`${field.name}-${value}`" class="checkbox-label">
				<input
					:id="`${field.name}-${value}`"
					:value="value"
					type="checkbox"
					:name="`${field.name}[]`"
					:checked="selectedValues.includes(value)"
					@change="updateSelection"
				/>
				{{ $t(`form${capitalize(value)}`) }}
			</label>
		</div>
		<div v-if="field.allowOther" class="checkboxes block">
			<label :for="`${field.name}-other`" class="checkbox-label">
				<input
					:id="`${field.name}-other`"
					:checked="isOtherSelected"
					name="otherSpecify"
					type="checkbox"
					class="toggle-extra-option"
					@change="toggleOther"
				/>
				{{ $t('formOtherSpecify') }}
			</label>
			<input type="text" class="extra-option" :disabled="!isOtherSelected" @input="updateOtherText" />
		</div>
	</div>
</template>

<script setup lang="ts">
const selectedValues = ref<string[]>([]);
const isOtherSelected = ref(false);
const otherTextValue = ref('');
const model = defineModel<string[]>('checkboxgroup');

defineProps<{
	field: FormField;
}>();

/**
 * Update the selected values based on checkbox changes.
 */
const updateSelection = (event: Event) => {
	const value = (event.target as HTMLInputElement).value;
	const isChecked = (event.target as HTMLInputElement).checked;

	if (isChecked && !selectedValues.value.includes(value)) {
		selectedValues.value.push(value);
	} else {
		selectedValues.value = selectedValues.value.filter((item) => item !== value);
	}

	model.value = selectedValues.value;
};

/**
 * Toggle the "Other" option and toggle the value in the selected values.
 */
const toggleOther = () => {
	isOtherSelected.value = !isOtherSelected.value;

	setOtherState();

	model.value = selectedValues.value;
};

/**
 * Update the "Other" text input and update the selected values accordingly.
 */
const updateOtherText = (event: Event) => {
	otherTextValue.value = (event.target as HTMLInputElement).value.trim();

	setOtherState();

	model.value = selectedValues.value;
};

/**
 * Set the "Other" option in the selected values to match the checkbox and
 * its text input.
 */
const setOtherState = () => {
	// We will replace any existing "other" value with the new one, or remove it if not selected.
	selectedValues.value = selectedValues.value.filter((item) => !item.startsWith('other:'));

	if (isOtherSelected.value && otherTextValue.value) {
		selectedValues.value.push(`other:${otherTextValue.value}`);
	}
};
</script>

<style lang="scss" scoped>
.formgroup-container {
	width: 100%;
	margin: 0 0 25px;

	.formgroup-heading {
		color: var(--body-color);
		margin: 0 0 10px;
	}

	.checkboxes {
		display: grid;
		grid-template-columns: repeat(2, 1fr);
		gap: 5px 20px;

		&.block {
			display: block;
			margin: 10px 0 0;
		}

		.extra-option {
			margin: 10px 0 0;
		}

		.checkbox-label {
			width: 100%;
			padding: 0;
			margin: 0;
			display: flex;
			gap: 3px;
			align-items: center;
			font-size: 0.9em;
			cursor: pointer;

			input {
				position: relative;
				display: inline-grid;
				place-content: center;
				flex-shrink: 0;
				width: 16px;
				height: 16px;
				padding: 0;
				margin-right: 5px;
				overflow: visible;
				appearance: none;
				border: 1px solid var(--body-color);
				border-radius: 3px;
				background: none;
				cursor: pointer;
			}

			input::before {
				content: '';
				position: absolute;
				inset: 2px;
				background: var(--body-color);
				border-radius: 2px;
				opacity: 0;
				transition: opacity 0.12s ease-in-out;
			}

			input:checked::before {
				opacity: 1;
			}

			input:focus-visible {
				outline: 1px solid var(--body-color);
				outline-offset: 1px;
			}
		}
	}
}

@media (max-width: 500px) {
	.formgroup-container {
		.checkboxes {
			grid-template-columns: 1fr;
		}
	}
}
</style>
